import React, { useContext, useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { useTranslation } from 'react-i18next';
import ChangePassword from './ChangePassword';
import { Dimmer, Divider, Header, Icon, Loader, Message } from 'semantic-ui-react';
import TwoFactorAuthentication from './TwoFactorAuthentication';
import notify from '~/components/Notify';
import ProfileSettingsService from '~/api/services/profileSettings';
import { useAuth } from '~/routes/components/AuthProvider';
import { StyledSection, CountdownWrapper } from './style';
import { El } from '~/components/Layout/style';
import ReactCountdownClock from 'react-countdown-clock'
import { ThemeContext } from 'styled-components';
import InfoPopup from '~/components/InfoPopup';
import { FARMER_ROLE_SLUG } from '~/Constants';
import AuthService from '~/api/services/auth';
import { handleErrors } from '~/utils/helpers';

interface ProfileSettingsProps { }

const ProfileSettings: React.FunctionComponent<ProfileSettingsProps> = () => {
  const { t } = useTranslation();
  const [isBusy, setIsBusy] = useState(false)
  const [data, setData] = useState(null)
  const [qrSvg, setQrSvg] = useState()
  const { setSystemData, systemData: { logged: { roleSlug } } } = useAuth()
  const { firstColor } = useContext(ThemeContext)
  const [qrFarmerCode, setQrFarmerCode] = useState(null)
  const [loadingFarmerQrCode, setLoadingFarmerQrCode] = useState(false)
  const farmerLogged = FARMER_ROLE_SLUG === roleSlug

  useEffect(() => {
    fetchSettingsData()
  }, [])

  async function fetchSettingsData() {
    setIsBusy(true)
    try {
      let responseQr
      const response = await ProfileSettingsService.getProfileSettingsOptions()
      if (response?.data?.tfaType === 'email') {
        responseQr = await ProfileSettingsService.getProfileSettingsQrCode()
        setQrSvg(responseQr.data)
      }
      setData(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function fetchSystemData() {
    try {
      const { data: response } = await AuthService.getSystemData();
      setSystemData({
        ...response,
        openedLoginPopups: true
      })
    } catch (error) {
      handleErrors(error)
    }
  }

  async function handelGoogleAuth(data, disable?) {
    setIsBusy(true)
    try {
      if (!disable) {
        await ProfileSettingsService.enableGoogleAuth(data)
        notify(t('Google auth. has been successfully enabled for your account'), 'success')
      } else {
        await ProfileSettingsService.disableGoogleAuth(data)
        notify(t('Google auth. has been successfully disabled for your account'), 'success')
      }
      fetchSettingsData()
      fetchSystemData()
    } catch (error) {
      if (error.response?.data?.message) {
        notify(error.response?.data?.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setIsBusy(false)
  }

  async function handleSmsAuth(payload, disable?) {
    setIsBusy(true)
    try {
      if (disable) {
        await ProfileSettingsService.disableSms(payload)
        notify(t('SMS has been successfully disabled for your account'), 'success')
      } else {
        if (data?.mobileConfirmed === 1) {
          await ProfileSettingsService.enableSms(payload)
          notify(t('SMS has been successfully enabled for your account'), 'success')
        } else {
          await ProfileSettingsService.confirmMobileCode(payload)
        }
      }
      fetchSettingsData()
      fetchSystemData()
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function generateFarmerQrCode() {
    setLoadingFarmerQrCode(true)
    try {
      const response = await ProfileSettingsService.generateQrCodeForFarmer()
      const encodedData = window.btoa(response.data);
      setQrFarmerCode(encodedData)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingFarmerQrCode(false)
  }

  function noQrCodeMessage() {
    return <Message info style={{ margin: '50px auto', textAlign: 'center', width: 'fit-content' }}>
      <p>{t('Click on button to generate QR code.')}</p>
    </Message>
  }

  function renderFarmerQrCodeSvg() {
    return <div style={{ display: 'flex' }}>
      <div>
        <StyledSection>
          <p>{t('Scan this barcode with your camera app')}</p>
        </StyledSection>
        <StyledSection>
          <img src={`data:image/svg+xml;base64,${qrFarmerCode}`} alt='' />
        </StyledSection>
        <StyledSection>
        </StyledSection>
      </div>
      <CountdownWrapper>
        <div className='text'>
          {t('Time QR code is valid')}
        </div>
        <div className='countdown'>
          <ReactCountdownClock seconds={120}
            color={firstColor}
            alpha={0.9}
            size={110}
            onComplete={() => setQrFarmerCode(null)} />
        </div>
      </CountdownWrapper>
    </div>
  }

  function renderLoadingQrCode() {
    return <Dimmer inverted active={loadingFarmerQrCode}>
      <Loader active={loadingFarmerQrCode} />
    </Dimmer>
  }

  function renderHeader() {
    return <div style={{ display: 'flex', alignItems: 'center' }}>
      <Icon name='sign in alternate' size='large' color='blue' />
      <Header style={{ margin: 0 }} as={'h4'}>{t('MyMilk.Farm connection')}</Header>
      <InfoPopup
        content={t('Create QR code which you can you use to login to MyMilk.Farm')}
        styleIcon={{ fontSize: '1.2em', marginLeft: 5, cursor: 'pointer' }}
      />
    </div>
  }

  function renderFarmerQrCode() {
    return <>
      {renderHeader()}
      {!qrFarmerCode && <El.PageHeaderActionButton
        style={{ float: 'right' }}
        onClick={() => generateFarmerQrCode()}>
        {t('Generate QR code')}
      </El.PageHeaderActionButton>}
      <div style={{ position: 'relative', minHeight: 150 }}>
        {!qrFarmerCode && noQrCodeMessage()}
        {!qrFarmerCode && renderLoadingQrCode()}
        {qrFarmerCode && !loadingFarmerQrCode && renderFarmerQrCodeSvg()}
      </div>
    </>
  }

  return <Layout
    isBusy={isBusy}
    segmented
    title={t('Profile settings')}
  >
    {Boolean(data?.farmerMobileAppConnectionAvailable) && farmerLogged && <>
      {renderFarmerQrCode()}
      <Divider />
    </>}
    {data && <ChangePassword passwordSecurity={data.passwordSecurity} />}
    <Divider />
    {data?.mobileNumber && <TwoFactorAuthentication
      data={data}
      handleSmsAuth={handleSmsAuth}
      isBusy={isBusy}
      handelGoogleAuth={handelGoogleAuth}
      qrSvg={qrSvg}
    />}
  </Layout>;
};

export default ProfileSettings;
